/**
 * TanStack Query Provider
 *
 * Vite + React 환경에 최적화된 간단한 QueryClient 설정을 제공합니다.
 */

import {
  MutationCache,
  QueryCache,
  QueryClient,
  QueryClientProvider,
  QueryClientProviderProps,
} from '@tanstack/react-query';
import { App } from 'antd';
import { HTTPError } from 'ky';
import { useState } from 'react';

type MessageInstance = ReturnType<typeof App.useApp>['message'];

/**
 * 글로벌 쿼리 에러 핸들러
 * 401 에러는 ky에서 이미 처리하므로 제외
 *
 * @param overrideMessage mutation의 `meta.errorMessage`로 전달된 커스텀 문구.
 *   지정 시 서버 에러 메시지 대신 이 문구를 노출한다.
 */
function createQueryErrorHandler(messageApi: MessageInstance) {
  return (error: Error, overrideMessage?: string) => {
    // HTTPError 타입 체크
    if (error instanceof HTTPError) {
      const { response } = error;

      // 401은 ky의 onError에서 이미 처리됨 (로그아웃 및 리다이렉트)
      if (response.status === 401) {
        return;
      }

      // 기타 HTTP 에러는 메시지 표시
      void messageApi.error({
        content: overrideMessage ?? (error.message || '요청 처리 중 오류가 발생했습니다.'),
        className: 'whitespace-pre-wrap',
      });
    } else {
      // 네트워크 에러 등
      void messageApi.error(overrideMessage ?? '네트워크 오류가 발생했습니다.');
    }
  };
}

/**
 * QueryClient 인스턴스
 * CSR 환경에서 직접 생성하여 사용합니다.
 */
function createQueryClient(messageApi: MessageInstance) {
  const handleQueryError = createQueryErrorHandler(messageApi);

  return new QueryClient({
    defaultOptions: {
      queries: {
        // 데이터가 1초 동안 fresh 상태로 유지됨
        staleTime: 1000,
        // 실패 시 최대 1회 재시도
        retry: 1,
      },
    },
    queryCache: new QueryCache({
      onError: (error, query) => {
        // 자체 에러 UI(재시도 패널)를 가진 조회는 전역 토스트를 억제한다 — opt-in(meta)
        if (query.meta?.suppressGlobalError) {
          return;
        }
        handleQueryError(error);
      },
    }),
    mutationCache: new MutationCache({
      // mutation에 meta.errorMessage가 있으면 서버 메시지 대신 해당 문구를 노출
      onError: (error, _variables, _context, mutation) => {
        // 인라인 에러 UI(확인 다이얼로그 등)를 직접 가진 mutation은 전역 토스트를 억제 — opt-in(meta)
        if (mutation.meta?.suppressGlobalError) {
          return;
        }
        handleQueryError(error, mutation.meta?.errorMessage);
      },
    }),
  });
}

/**
 * TanStack Query Provider 컴포넌트
 *
 * 모든 하위 컴포넌트에서 useQuery, useMutation 등의 TanStack Query 훅을 사용할 수 있게 해줍니다.
 */
export default function QueryProvider({ children }: Pick<QueryClientProviderProps, 'children'>) {
  const { message } = App.useApp();
  const [queryClient] = useState(() => createQueryClient(message));

  return <QueryClientProvider client={queryClient}>{children}</QueryClientProvider>;
}
